/* eslint-disable jsx-a11y/aria-role -- Should add these for accessibility but don't want to change this file to much */

import { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { LoaderTypes } from 'Components/UI_Library/LoadingIndicators/Loader/Loader';

import Loader from 'Components/UI_Library/LoadingIndicators/Loader';
import classes from './Button.module.css';

type ButtonType =
    | 'primary'
    | 'white'
    | 'white-condensed'
    | 'white-square'
    | 'white-circle'
    | 'ghost'
    | 'dark'
    | 'darkNavy'
    | 'outlined'
    | 'primary-thin'
    | 'success'
    | 'secondaryTable'
    | 'secondary'
    | 'flat'
    | 'cancel'
    | 'cancel-condensed'
    | 'danger'
    | 'danger-light'
    | 'info'
    | 'red'
    | 'transparent'
    | 'tertiary'
    | 'tertiary mobileChip' // FIXME: Replace this with support for array instead...
    | 'link'
    | 'link-cancel'
    | 'lightBlue'
    | 'bulk-blue'
    | 'bulk-cancel'
    | 'disabled'
    | 'white mobileChip';

type TooltipClass = 'tooltipLeft' | 'tooltipRight' | 'tooltipCentre' | null;

type Props = {
    /** pass in a number as a string e.g. "3000" */
    autoplayTimer?: string;
    /** pass in a string or JSX element. Shows a tooltip on hover of button  */
    tooltip?: string | ReactElement;
    /** Only supported if href property is not provided */
    onClick?: (
        e?: Parameters<NonNullable<JSX.IntrinsicElements['button']['onClick']>>[0]
    ) => ReturnType<NonNullable<JSX.IntrinsicElements['button']['onClick']>>;
    /** pass in either 'primary', 'white', 'ghost', 'dark', 'darkNavy', 'outlined', 'primary-thin', 'success', 'secondaryTable', 'secondary', 'flat', 'cancel', 'danger', 'danger-light', 'info', 'red', 'transparent', 'tertiary', 'link', 'link-cancel', 'lightBlue', 'bulk-blue', 'bulk-cancel', 'disabled' */
    type?: ButtonType;
    /** pass in a custom className */
    className?: string;
    /** pass in CSS custom styles */
    style?: CSSProperties;
    /** disables the button */
    disabled?: boolean;
    /** Renders the <Loader> if true */
    loading?: boolean;
    /** Pass in a custom loader type style, defaults to 'in-button blue' */
    loaderType?: LoaderTypes;
    /** adds the focus state the button on load */
    autofocus?: boolean;
    /** adds a selected class */
    selected?: boolean;
    /** adds ID to the button */
    id?: string;
    /** Takes in a JSX element, renders it to the left of the button text */
    iconLeft?: ReactElement | null;
    /** Takes in a JSX element, renders it to the right of the button text */
    iconRight?: ReactElement;
    /** Takes in a JSX element, renders it in center */
    icon?: ReactElement;
    /** Text of the button */
    text?: string;
    /** Title of the button */
    title?: string;
    /** Takes in a JSX element, standard React prop. Usually used instead of text prop */
    children?: ReactNode | string;
    /** Location the button navigates to\ */
    href?: string;
    /** Location the button navigates to\ */
    testId?: string;
} & (
    | // Prevent onClickSideEffect without href and onClick with href
    {
          /**
           * If href is passed we [don't support
           * onClick](https://bitbucket.org/Paytron/paytron/src/98fab806060a23e1228286d01fd36aba73e04970/ptrn-fe/src/Components/UI_Library/FormElements/Button.tsx).
           */
          href: string;
          /** side effect for when button uses `href` */
          onClickSideEffect?: () => void;
          /**
           * If href is passed we [don't support
           * onClick](https://bitbucket.org/Paytron/paytron/src/98fab806060a23e1228286d01fd36aba73e04970/ptrn-fe/src/Components/UI_Library/FormElements/Button.tsx).
           */
          onClick?: never;
      }
    | { href?: never; onClickSideEffect?: never }
);

const Button = (props: Props) => {
    const button = useRef<HTMLButtonElement>(null);
    const tooltip = useRef<HTMLSpanElement>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ left: string; top: string } | boolean>(false);
    const [tooltipClass, setTooltipClass] = useState<TooltipClass>(null);
    const [buttonHover, setButtonHover] = useState(false);

    useEffect(() => {
        let timer: null | ReturnType<typeof setTimeout> = null;
        if (props.autoplayTimer) {
            timer = setTimeout(() => {
                props.onClick && props.onClick();
            }, parseFloat(props.autoplayTimer));
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [props.autoplayTimer]);

    const calculateLeftPosition = (buttonPos: DOMRect, tooltipPos: DOMRect) => {
        const closeToLeft = buttonPos.left < tooltipPos.width;
        const closeToRight = window.innerWidth - buttonPos.left < tooltipPos.width;

        if (closeToLeft) {
            // align tooltip to left of button
            setTooltipClass('tooltipLeft');
            return buttonPos.left;
        }

        if (closeToRight) {
            // align tooltip to right of button
            setTooltipClass('tooltipRight');
            return buttonPos.left - buttonPos.width / 2;
        }

        const buttonCentre = buttonPos.left + buttonPos.width / 2;
        const tooltipCentre = tooltipPos.width / 2;

        // align tooltip to centre of button
        setTooltipClass('tooltipCentre');
        return buttonCentre - tooltipCentre;
    };

    const calculateTooltipPosition = () => {
        const buttonPos = button?.current?.getBoundingClientRect();
        const tooltipPos = tooltip?.current?.getBoundingClientRect();
        if (buttonPos && tooltipPos) {
            setTooltipPosition({
                left: `${calculateLeftPosition(buttonPos, tooltipPos)}px`,
                top: `${buttonPos.top - buttonPos.height}px`,
            });
        }
    };

    // initiate tooltip when the prop is there and on button hover
    useEffect(() => {
        if (props.tooltip) {
            calculateTooltipPosition();
        }
    }, [props.tooltip, buttonHover]);

    const getClasses = () => {
        let classNames = `${classes.button}`;
        if (props.type) {
            // FIXME: should just make type prop an array instead
            if (props.type === 'tertiary mobileChip') {
                classNames += ` ${classes.tertiary}`;
                classNames += ` ${classes.mobileChip}`;
            } else if (props.type === 'white mobileChip') {
                classNames += ` ${classes.white}`;
                classNames += ` ${classes.mobileChip}`;
            } else {
                classNames += ` ${classes[props.type]}`;
            }
        }
        if (props.selected) {
            classNames += ` ${classes.selected}`;
        }
        if (props.className) {
            classNames += ` ${props.className}`;
        }
        if (props.disabled && props.href) {
            classNames += ` ${classes.disabledState}`;
        }
        return classNames;
    };

    const className = getClasses();

    const buttonProps = {
        'data-testid': props.testId || 'button',
        onMouseOver: () => setButtonHover(true),
        onMouseOut: () => setButtonHover(false),
        className,
        disabled: props.disabled,
        style: props.style,
        autoFocus: props.autofocus,
        id: props.id,
        ref: button,
        'data-toggle': 'tooltip',
        'data-placement': 'bottom',
        title: props.title,
        children: (
            <>
                {props.iconLeft && props.iconLeft}
                <span className={classes.wrapper}>
                    {props.text}
                    {props.children}
                    {props.icon && props.icon}
                </span>
                {props.iconRight && props.iconRight}
                {props.loading && props?.loaderType !== 'none' && <Loader type={props.loaderType} />}
                {props.autoplayTimer && (
                    <div className={classes.autoplay} style={{ animationDuration: `${props.autoplayTimer}ms` }} />
                )}
            </>
        ),
    };

    return (
        <>
            {props.href ? (
                <Link
                    style={props.disabled ? { pointerEvents: 'none' } : {}}
                    to={props.href}
                    onClick={props.onClickSideEffect}
                >
                    <span {...buttonProps} />
                </Link>
            ) : (
                <button type="button" {...buttonProps} onClick={props.onClick} />
            )}
            {props.tooltip &&
                ReactDOM.createPortal(
                    <span
                        ref={tooltip}
                        className={`${classes.tooltip} ${buttonHover ? classes.tooltipHover : ''} ${
                            tooltipClass ? classes[tooltipClass] : ''
                        }`}
                        style={typeof tooltipPosition !== 'boolean' ? tooltipPosition : {}}
                        role="portal"
                    >
                        {props.tooltip}
                    </span>,
                    document.body
                )}
        </>
    );
};

Button.defaultProps = {
    type: 'primary' satisfies ButtonType,
    loaderType: 'in-button blue' satisfies LoaderTypes,
};

/* eslint-enable jsx-a11y/aria-role -- Should add these for accessibility but don't want to change this file to much */

export default Button;
